import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { MessageCircle, Calculator, ShieldCheck, Clock, Landmark, ChevronDown, Plane } from "lucide-react";
import { waLink, IMAGES } from "./constants";
import { scrollToSection } from "./Reveal";

const LINES = [
  <>Finding the mortgage</>,
  <>
    that fits <span className="text-amber-400">your life.</span>
  </>,
];

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.35 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 140, damping: 16 });
  const sry = useSpring(ry, { stiffness: 140, damping: 16 });

  const onTilt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rx.set(py * -10);
    ry.set(px * 12);
  };
  const resetTilt = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Kieran Woolley with a coffee, new-build homes through the window behind him"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#04222C]/80 via-[#04222C]/60 to-[#04222C]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#04222C]/80 via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.05)} className="mb-6">
            <a
              href="#aviation"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#aviation");
              }}
              data-testid="hero-aviation-badge"
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 backdrop-blur px-4 py-2 text-xs font-semibold text-amber-300 transition-colors duration-300 hover:bg-amber-400/20"
            >
              <Plane size={14} />
              Aviation specialist — pilots, cabin crew & engineers
            </a>
          </motion.div>
          <motion.p
            {...fade(0.15)}
            data-testid="hero-overline"
            className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-amber-400/90 mb-6"
          >
            Independent Advice, One-to-One with Kieran
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-[1.08]"
          >
            {LINES.map((line, i) => (
              <MaskedLine key={i} index={i}>
                {line}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            {...fade(0.85)}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            I'm Kieran. I'll search over 90 UK lenders to find the mortgage
            that genuinely suits you — and you can WhatsApp me directly whenever
            you need a hand. My advice is always personal to you.
          </motion.p>
          <motion.div {...fade(1)} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollToSection("#calculator")}
              data-testid="hero-calculator-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-semibold text-[#03222D] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_32px_rgba(229,191,182,0.35)]"
            >
              <Calculator size={17} />
              Try the Calculator
            </button>
            <a
              href={waLink("Hi Kieran, I'd like some mortgage advice.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/5 backdrop-blur px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-[#25D366]/60 hover:bg-[#25D366]/10"
            >
              <MessageCircle size={17} className="text-[#25D366]" />
              Message Me on WhatsApp
            </a>
          </motion.div>
          <motion.div
            {...fade(1.15)}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 font-mono text-xs tracking-widest uppercase text-slate-400"
          >
            <span data-testid="hero-stat-lenders">
              <span className="text-white text-base font-semibold tracking-normal">90+</span>
              <span className="block mt-1">UK Lenders</span>
            </span>
            <span data-testid="hero-stat-aip">
              <span className="text-white text-base font-semibold tracking-normal">24 hrs</span>
              <span className="block mt-1">Agreement in Principle</span>
            </span>
            <span data-testid="hero-stat-fee">
              <span className="text-white text-base font-semibold tracking-normal">5★</span>
              <span className="block mt-1">Client Rated</span>
            </span>
          </motion.div>
        </div>

        <motion.div
          style={{ y: cardY }}
          className="lg:col-span-5 hidden lg:block"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div style={{ perspective: 1100 }}>
            <motion.div
              data-testid="hero-market-card"
              onMouseMove={onTilt}
              onMouseLeave={resetTilt}
              style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
              className="rounded-3xl border border-white/10 bg-[#06303F]/70 backdrop-blur-xl p-8 shadow-2xl shadow-black/40"
            >
              <div className="flex items-center justify-between mb-7">
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-400">
                  What to expect
                </p>
                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(0,230,165,0.8)]" />
              </div>
              <div className="space-y-6" style={{ transform: "translateZ(30px)" }}>
                {[
                  {
                    icon: Landmark,
                    title: "The whole market, checked for you",
                    desc: "90+ lenders compared, including deals you won't find online",
                  },
                  {
                    icon: Clock,
                    title: "Quick answers when it matters",
                    desc: "Agreement in principle, usually within 24 hours",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Just me, start to finish",
                    desc: "You deal with me directly — never a call centre",
                  },
                ].map((f) => (
                  <div key={f.title} className="flex gap-4">
                    <div className="shrink-0 h-11 w-11 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center">
                      <f.icon size={18} className="text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.title}</p>
                      <p className="text-sm text-slate-400 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                <p className="font-mono text-[11px] tracking-widest uppercase text-slate-500">
                  Straight answers, no pressure
                </p>
                <a
                  href={waLink("Hi Kieran, fancy a chat about my mortgage options?")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-card-whatsapp-link"
                  className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                >
                  Say hello
                </a>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToSection("#advice")}
        data-testid="hero-scroll-cue"
        aria-label="Scroll down"
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-slate-500 hover:text-amber-400 transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown size={22} />
      </motion.button>
    </section>
  );
}
